/**
 * useDocument.ts — React binding for an open-document session.
 *
 * Owns the DocSession state machine (`loading → ready | error`) and the
 * subscriber that keeps it in sync with remote edits. Callers get back
 * `{ session, patch, save }` and render a loading skeleton while
 * `session.status === 'loading'`.
 *
 * The hook is adapter-agnostic — local and remote adapters expose the same
 * surface, so this hook doesn't branch. Construct the right adapter in the
 * route component (`DocRoute`) based on `useAuth()`; this hook just drives it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ScenarioBundle } from '../engine/types';
import type { DocumentAdapter, DocPatch, DocSession } from './DocumentAdapter';

export interface UseDocumentResult {
  session: DocSession;
  /** Submit an edit. Routes through the adapter (local apply or remote POST). */
  patch: (patch: DocPatch) => void;
  /** Replace the whole bundle (Save / Save As). */
  save: (bundle: ScenarioBundle) => Promise<void>;
}

export function useDocument(adapter: DocumentAdapter | null, docId: string | null): UseDocumentResult {
  const [session, setSession] = useState<DocSession>(
    docId ? { docId, bundle: null, status: 'loading' } : { docId: '', bundle: null, status: 'error', error: 'No document id.' },
  );
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  // Open + subscribe on mount / when adapter or docId changes.
  useEffect(() => {
    if (!adapter || !docId) return;
    let cancelled = false;
    setSession({ docId, bundle: null, status: 'loading' });

    adapter
      .open(docId)
      .then((s) => {
        if (cancelled) return;
        setSession(s);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setSession({ docId, bundle: null, status: 'error', error: e instanceof Error ? e.message : String(e) });
      });

    const unsub = adapter.subscribe(docId, (bundle) => {
      if (cancelled) return;
      setSession((prev) => (prev.docId === docId ? { ...prev, bundle, status: 'ready' } : prev));
    });

    return () => {
      cancelled = true;
      unsub();
      // Leave the socket open in case the adapter is reused; `close` is the
      // explicit teardown. We only unsubscribe the React-owned listener above.
    };
  }, [adapter, docId]);

  const patch = useCallback(
    (patch: DocPatch) => {
      if (!docId) return;
      adapterRef.current?.patch(docId, patch);
    },
    [docId],
  );

  const save = useCallback(
    async (bundle: ScenarioBundle) => {
      if (!docId) return;
      await adapterRef.current?.save(docId, bundle);
    },
    [docId],
  );

  return { session, patch, save };
}
